// modules
import FormatService from 'modules/Format/service';
import LiveDataService from 'modules/LiveData/service';
import { ITVPosition } from 'modules/TradingChart/components/TradingView/utils/providers/brokerProvider/positions/types';
import { broker } from 'modules/TradingChart/consts';
import { listenToLiveDataV2Changes } from 'modules/TradesPW3/utils';
import { WsPortfolioV2Feeder } from 'modules/WebSocket';
import { PositionBase } from 'modules/TradesPW3/Positions/typings';
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';
import { POSITION_TYPE } from 'modules/TradesPW3/Positions/constants';
import LoadingStore from 'modules/Loading';
import AppService from 'modules/App/service';
import { OrderSide } from 'modules/Ordering';
import {
  Side as TvSide,
} from 'modules/TradingChart/components/TradingView/sources/charting_library';
import PositionsService from '../positions';

const findPositionsByInstrumentKey = (
  positions: PositionBase[],
  instrumentKey: string,
) => positions.filter(position => position.instrument_key === instrumentKey);

class PositionsTVService {
  [x: string]: any;
  fetchTVFormattedPositions = async () => {
    await PositionsService.getPositions();

    this.subscribeForScrips(
      PositionsService.groupedPositionsByType.openPositions,
    );
    this.subscribeForScrips(
      PositionsService.groupedPositionsByType.closedPositions,
    );

    return this.formatPositionsToTVTable(
      PositionsService.groupedPositionsByType.openPositions,
    );
  };

  fetchTVFormattedClosedPositions = (): ITVPosition[] =>
    this.formatPositionsToTVTable(
      PositionsService.groupedPositionsByType.closedPositions,
    );

  subscribeForScrips = (positions: PositionBase[]) => {
    const uniqueIds = positions.map(item => item.instrument_key || '');
    LiveDataService.subscribeScrip(uniqueIds, MARKET_FEEDER_MODES.LTPC);
  };

  formatPositionsToTVTable = (positions: PositionBase[]) =>
    positions.map(position => this.formatPositionToTVTable(position));

  mapOrderSideToTVside = (side: OrderSide) =>
    (side === OrderSide.Buy ? TvSide.Buy : TvSide.Sell);

  formatPositionToTVTable = (position: PositionBase): ITVPosition => {
    const positionDetails = PositionsService.generatePositionDetails(position);
    const {
      fillInfo: { cfBuy, dayBuy, cfSell, daySell },
      instrument: { e: segment, t: tvExchangeToken },
      product,
      instrument_key: instrumentKey,
    } = position;

    const exchangeToken = `${segment}|${tvExchangeToken}`;
    const name = position?.name || '';
    const {
      avgPrice,
      netQty,
      pnl: { unrealized, realized },
    } = positionDetails;
    const liveData = LiveDataService.getByInstrumentKey(instrumentKey || '');
    const lastTradedPrice = liveData?.lastTradedPrice || 0;

    const side = PositionsService.getPositionSide(position);

    const formattedSide = side
      ? this.mapOrderSideToTVside(side)
      : '-';

    const positionId = PositionsService.generatePositionUniqueId(position);

    const digitsAfterDot = FormatService.getDigitsAfterDot(exchangeToken);

    const getPriceRelatedToLtp = (value: number) =>
      (lastTradedPrice
        ? FormatService.formatToIndianFloat(value, digitsAfterDot)
        : '-');

    const avgTradePrice = FormatService.getRoundOffToTwoDecimal(
      avgPrice,
      digitsAfterDot,
    );

    return {
      id: positionId,
      symbol: position?.tvExchangeToken || '',
      exchangeToken: exchangeToken || '',
      name,
      segment,
      side: formattedSide,
      product_type: PositionsService.getProductValue(product),
      netQty,
      avg_trade_price: FormatService.formatToIndianFloat(
        avgTradePrice,
        digitsAfterDot,
      ),
      ltp: FormatService.formatToIndianFloat(lastTradedPrice, digitsAfterDot),
      realised_pnl: getPriceRelatedToLtp(realized),
      unrealised_pnl: getPriceRelatedToLtp(unrealized),
      // Closed positions property
      buy_avg_price: FormatService.formatToIndianFloat(
        dayBuy?.avgPrice ?? 0,
        digitsAfterDot,
      ),
      sell_avg_price: FormatService.formatToIndianFloat(
        daySell?.avgPrice ?? 0,
        digitsAfterDot,
      ),
      total_buy_qty: Number(cfBuy?.qty ?? 0) + Number(dayBuy?.qty ?? 0),
      total_sell_qty: Number(cfSell?.qty ?? 0) + Number(daySell?.qty ?? 0),
      // Internal TV property
      qty: netQty,
      avgPrice: avgTradePrice,
      pnl: FormatService.getRoundOffToTwoDecimal(unrealized, digitsAfterDot),
      // Sort props
      ltpSortProp: FormatService.getRoundOffToTwoDecimal(
        lastTradedPrice,
        digitsAfterDot,
      ),
      realisedPnlSortProp: FormatService.getRoundOffToTwoDecimal(
        realized,
        digitsAfterDot,
      ),
      buyAvgPriceSortProp: FormatService.getRoundOffToTwoDecimal(
        dayBuy?.avgPrice ?? 0,
        digitsAfterDot,
      ),
      sellAvgPriceSortProp: FormatService.getRoundOffToTwoDecimal(
        dayBuy?.avgPrice ?? 0,
        digitsAfterDot,
      ),
    };
  };

  getFormattedPositionsPnL = () => {
    let dailyPositionsPnL = 0;
    let totalPositionsPnL = 0;

    if (PositionsService.positions.length) {
      const { daily, total } = PositionsService.getGlobalPnl(
        PositionsService.positions,
      );
      dailyPositionsPnL = daily;
      totalPositionsPnL = total;
    }

    return {
      daily: FormatService.addRupeeSign(
        FormatService.formatToIndianFloat(dailyPositionsPnL, 2, true),
        true,
      ),

      total: FormatService.addRupeeSign(
        FormatService.formatToIndianFloat(totalPositionsPnL, 2, true),
        true,
      ),
    };
  };

  openClosePositionModal = (positionId: string) => {
    PositionsService.setClosingPositionId(positionId);
    AppService.toggleModal('closePosition');
  };
}

const positionsTVService = new PositionsTVService();
export default positionsTVService;

listenToLiveDataV2Changes(uniqueId => {
  const updateTvPositions = (
    positions: PositionBase[],
    type: POSITION_TYPE,
  ) => {
    const positionList = findPositionsByInstrumentKey(positions, uniqueId);

    if (positionList.length) {
      positionList.forEach(currentPosition => {
        const formattedTvPosition = positionsTVService.formatPositionToTVTable(
          currentPosition,
        );

        if (broker.instance) {
          broker.instance.updatePositionsPnL();

          if (type === POSITION_TYPE.Opened) {
            broker.instance.updateOpenPositions([formattedTvPosition]);
            broker.instance.plUpdate(
              formattedTvPosition.id,
              formattedTvPosition.pnl,
            );
          } else {
            broker.instance.updateClosedPositions([formattedTvPosition]);
          }
        }
      });
    }
  };

  if (
    WsPortfolioV2Feeder.connected &&
    !LoadingStore.getLoading('GET_POSITIONS')
  ) {
    updateTvPositions(
      PositionsService.groupedPositionsByType.openPositions,
      POSITION_TYPE.Opened,
    );
    updateTvPositions(
      PositionsService.groupedPositionsByType.closedPositions,
      POSITION_TYPE.Closed,
    );
  }
});
